/// <amd-module name="Search/Package/Components/PackageItem" />

import * as  React from "react";
import * as Resources from "Search/Package/Resources";
import { VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";
import { PackageResult, PackageHit } from "Search/WebApi/Contracts/Search.Shared";
import { format } from "VSS/Core/Util/String";
import { Link } from "VSSUI/Link";
import { Icon } from "VSSUI/Icon";
import { css } from "VSSUI/Util";
import { htmlEncode } from "Search/Shared/Utils";
import { SearchResultItemComponent } from "SearchUI/SearchResultItem";
import { getAssetLocation } from "VSS/Platform/Location";

const WHITE_SPACE_REGEX = /(\s+)/g;
const NEW_LINE_REGEX = /[.]*[\s]*(\n+)/g;
const HIGHLIGHTSTARTTAG = "<highlighthit>";
const HIGHLIGHTENDTAG = "</highlighthit>";
const HIT_HIGHT_LIGHT_HTML_ENCODED_START_TAG_REGEX = /(&lt;highlighthit&gt;)/gi;
const HIT_HIGHT_LIGHT_HTML_ENCODED_END_TAG_REGEX = /(&lt;\/highlighthit&gt;)/gi;
const ELLIPSIS = "...";
const ELLIPSISWITHSPACE = "... ";
// MAXCHARINVIEW is the maximum number of characters that will be displayed before searchHit
const MAXCHARACTERINVIEW = 75;

export interface PackageItemProps extends IVssComponentProperties {
    packageSearchResult: PackageResult;
    onRenderComplete(): void;
    onPackageItemClicked(link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void;
}

export class PackageItem extends VssComponent<PackageItemProps, {}> {

    public render(): JSX.Element {
        const result = this.props.packageSearchResult;
        const hits = convertHighLightsArrayToDictionary(result.hits);

        const hightlightedName = this._getContentWithHighlighting(hits.name, result.name);
        const highlightedDescription = this._getContentWithHighlighting(hits.description, result.description);

        let protocolIconDetails = this._getProtocolIconDetails(result.protocolType);

        const titleAriaLabel = format(Resources.PackageAriaLabel, result.name);
        const titleLink = this._getFeedLink(result, 0);
        const titleComponent = (
            <span>
                {result.feeds[0].feedName}{" > "}{hightlightedName}
            </span>);
        const titleComponentWithLink = getTitleComponent(titleLink, titleComponent, this.props.onItemPackageClicked, titleAriaLabel);

        return (
            <div className="package-result flex flex-column">
                <SearchResultItemComponent
                    titleComponent={titleComponentWithLink}
                    imageComponent={protocolIconDetails}
                    titleMetaDataComponent={undefined}
                    descriptionComponent={highlightedDescription}
                />
            </div>
        );
    }

    public componentDidMount(): void {
        if (this.props.onRenderComplete) {
            this.props.onRenderComplete();
        }
    }

    public componentDidUpdate(): void {
        if (this.props.onRenderComplete) {
            this.props.onRenderComplete();
        }
    }

    private _getProtocolIconDetails(protocol: string): JSX.Element {
        const protocolLower = protocol.toLowerCase();
        
        if (protocolLower === "nuget") {
            return this._getProtocolFontIcon("NugetLogo");
        } else if (protocolLower === "npm") {
            return this._getProtocolFontIcon("NPMLogo");
        } else if (protocolLower === "maven") {
            return this._getProtocolFontIcon("ApacheMavenLogo");
        } else if (protocolLower === "gradle") {
            return this._getProtocolFontIcon("GradleLogo32");
        } else if (protocolLower === "pypi") {
            return this._getProtocolSvgIcon("python");
        } else if (protocolLower === "ivy") {
            return this._getProtocolSvgIcon("ivy");
        } else if (protocolLower === "cargo") {
            return this._getProtocolSvgIcon("rust");
        }

        // upack or unknown type: use a generic package icon
        return <Icon iconName={"Package"} className={"package-icon"} />
    }

    private _getProtocolFontIcon(protocolIcon: string): JSX.Element {
        return <Icon className={css("package-icon", protocolIcon)} iconName={protocolIcon} />;
    }

    private _getProtocolSvgIcon(name: string): JSX.Element {
        const imgSrcUrl = getAssetLocation(this.context.pageContext, `ms.vss-search-web/search-package-content/Images/icon-brand-${name}.svg`);
        return <> {imgSrcUrl && <img className="package-icon" src={imgSrcUrl} alt="" />} </>
    }

    private _getFeedLink(result: PackageResult, index: number): string {
        const url = result.feeds[index].packageUrl;
        return url;
    }

    private _getContentWithHighlighting(highlights: string[] | undefined, defaultFieldValue: string, shiftHitHighlightWithinView?: boolean): JSX.Element {
        if (defaultFieldValue == null) {
            defaultFieldValue = "";
        }
        return getSearchResultFieldElementWithHighlights(highlights, defaultFieldValue, shiftHitHighlightWithinView);
    }
}

function getTitleComponent(titleLinkUrl: string, titleComponent: JSX.Element, onItemPackageClicked: (link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>) => void, ariaLabel?: string): JSX.Element {
    return (<Link
        className={"search-result-title-link"}
        href={titleLinkUrl}
        onClick={evt => onItemPackageClicked(titleLinkUrl, evt)}
        aria-label={ariaLabel}
        removeUnderline={true}>
        {titleComponent}
    </Link>);
}

function getSearchResultFieldElementWithHighlights(
    highlights: string[] | undefined,
    defaultFieldValue: string,
    shiftHitHighlightWithinView?: boolean,
    maxCharacterInView = MAXCHARACTERINVIEW): JSX.Element {
    let concatSearchHighlights = _concatenateHighlightStrings(highlights);

    if (!concatSearchHighlights) {
        concatSearchHighlights = defaultFieldValue;
    }

    if (shiftHitHighlightWithinView) {
        const hitIndex = concatSearchHighlights.indexOf(HIGHLIGHTSTARTTAG);
        concatSearchHighlights = _getHitWithinView(concatSearchHighlights, hitIndex, maxCharacterInView);
    }

    return getComponentWithHighlighting(concatSearchHighlights);
}

function getComponentWithHighlighting(highlights: string): JSX.Element {
    return <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(highlights) }} />;
}

function sanitizeHtml(html: string): string {
    // replace multiple return characters with .
    html = html.replace(NEW_LINE_REGEX, ". ");
    // replace multiple white spaces with a single white space.
    html = html.replace(WHITE_SPACE_REGEX, " ");
    const encodedValue = htmlEncode(html);

    return encodedValue
        .replace(HIT_HIGHT_LIGHT_HTML_ENCODED_START_TAG_REGEX, HIGHLIGHTSTARTTAG)
        .replace(HIT_HIGHT_LIGHT_HTML_ENCODED_END_TAG_REGEX, HIGHLIGHTENDTAG);
}

function _getHitWithinView(concatSearchHighlights: string, hitIndex: number, maxCharInView: number): string {
    let result: string = concatSearchHighlights;
    if (hitIndex > maxCharInView) {
        let spaceIndex: number;
        // searching for a space or linefeed or newline before hit in preceding "maxCharInView" number of characters
        for (spaceIndex = hitIndex - maxCharInView; spaceIndex < hitIndex; spaceIndex++) {
            if (concatSearchHighlights[spaceIndex] === " " || _isCharacterLineFeed(concatSearchHighlights, spaceIndex)) {
                break;
            }
            else if (_isCharacterNewLine(concatSearchHighlights, spaceIndex)) {
                ++spaceIndex;
                break;
            }
        }

        // if space or linefeed or newline not found, display from the preceding "maxCharInView" number of characters of the hit.
        if (spaceIndex === hitIndex) {
            spaceIndex = hitIndex - maxCharInView - 1;
        }

        result = concatSearchHighlights.slice(spaceIndex + 1);
        if (!_isCharacterLineFeed(concatSearchHighlights, spaceIndex)) {
            result = ELLIPSIS.concat(result);
        }
    }
    return result;
}

function _isCharacterNewLine(str: string, index: number): boolean {
    return ((str.length >= index + 1) && str.slice(index, index + 2) === "\r\n");
}

function _isCharacterLineFeed(str: string, index: number): boolean {
    return (str[index] === "\n");
}

function _concatenateHighlightStrings(highlights: string[] | undefined): string | undefined {
    return highlights && highlights.length > 0 ? highlights.join(ELLIPSISWITHSPACE) : undefined;
}

function convertHighLightsArrayToDictionary(hits: PackageHit[], getFirstHighlight?: boolean): { [key: string]: string[] } {
    const result: { [key: string]: string[] } = {};

    for (const hit of hits) {
        if (getFirstHighlight && hit.highlights && hit.highlights.length > 0) {
            result[hit.fieldReferenceName] = [hit.highlights[0]];
        } else {
            result[hit.fieldReferenceName] = hit.highlights;
        }
    }
    return result;
}
